import { Modal } from '@mantine/core';
import styled from 'styled-components';
import type { ReactNode } from 'react';

interface Props {
	opened: boolean;
	onClose: () => void;
	title: string;
	children: ReactNode;
}

/**
 * The fullscreen shell the master-document editor lives in.
 *
 * A shell and nothing more. It used to mount two surfaces and own a tab bar above them, because
 * a PDF's field binding and signature placement were separate editors over the same file. They
 * are one editor now, and the Word document always was, so each owns its own tabs and the modal
 * is back to what it is for: a full-height box with a way out.
 *
 * `closeOnEscape={false}`: the editor can hold unsaved edits, and Escape would throw them away
 * without asking. The way out is the editor's own back arrow, which asks.
 */
export const DocumentEditorModal = ({ opened, onClose, title, children }: Props) => (
	<Modal
		opened={opened}
		onClose={onClose}
		fullScreen
		withCloseButton={false}
		closeOnEscape={false}
		title={title}
		styles={{
			// Flex layout (not calc(100vh - 60px)): the Mantine title-bar height isn't a
			// known constant, so a calc clips the footer.
			content: { display: 'flex', flexDirection: 'column', height: '100vh' },
			// A flex COLUMN, not just a bounded block. The editor inside sizes itself with
			// `height: 100%`, which needs a parent with a definite height to resolve against —
			// a block body left it growing to its content, its own scrollers never overflowed,
			// and this `overflow: hidden` quietly clipped whatever did not fit.
			body: {
				display: 'flex',
				flexDirection: 'column',
				flex: 1,
				minHeight: 0,
				padding: '0 1rem 1rem 1rem',
				overflow: 'hidden',
			},
		}}
	>
		<EditorSlot data-testid="document-editor-surface">{children}</EditorSlot>
	</Modal>
);

const EditorSlot = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
`;
